import { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Chip,
  CircularProgress,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Drawer,
  IconButton,
  Divider,
  Link,
  Alert,
  TableSortLabel,
  Tabs,
  Tab,
  Badge,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { audiencesApi } from '../../services/audiences';
import { proposedArticlesApi } from '../../services/proposed-articles';
import { retailerCatalogApi } from '../../services/retailerCatalog';
import { useToast } from '../../contexts/ToastContext';
import type { Audience, CatalogueMatchSignal, GroundedProduct, MeasurementResult } from '@traaker/shared';
import { highlightText, renderMarkdown, type HighlightTerm } from './highlight';
import { EngineLogo } from '../../components/brand/EngineLogo';
import { SIGNAL_ACCENT_COLOR } from './MatchConfigCard';
import { Flag, ZoneFlag } from '../dashboard/Flag';
import { languageFlag, resolveZone } from '../dashboard/geoFlags';

const COVERAGE_META: Record<string, { label: string; color: 'success' | 'warning' | 'info' | 'default' }> = {
  brand_covered: { label: 'Recommandé', color: 'success' },
  assortment_leaked: { label: 'Fuite', color: 'warning' },
  mentioned: { label: 'Mentionné sans lien', color: 'info' },
  not_covered: { label: 'Absent', color: 'default' },
};

type SortKey = 'score' | 'coverage' | 'engine';

/**
 * Results explorer for a measurement run — handles 1000+ rows: server-paginated
 * light list (segment/coverage filters), lazy "load more", per-page sort, and a
 * response drawer that fetches the full AI answer on demand + highlights product
 * names / SKUs / source URLs / the retailer domain.
 */
export function MeasurementExplorer({
  brandId,
  audience,
  runId,
}: {
  brandId: string;
  audience: Audience;
  runId: string;
}) {
  const [rows, setRows] = useState<MeasurementResult[]>([]);
  const [cursor, setCursor] = useState<string | undefined>();
  const [loading, setLoading] = useState(false);
  const [segmentId, setSegmentId] = useState('');
  const [coverage, setCoverage] = useState('');
  const [zone, setZone] = useState('');
  const [sortKey, setSortKey] = useState<SortKey>('score');
  const [sortAsc, setSortAsc] = useState(true);
  const [open, setOpen] = useState<MeasurementResult | null>(null);

  const segName = useCallback(
    (id: string) => audience.segments.find((s) => s.segmentId === id)?.name || id,
    [audience],
  );

  // Distinct zones across the audience's segments (filter dropdown options).
  const zoneOptions = useMemo(
    () => [...new Set(audience.segments.flatMap((s) => s.zones ?? []).filter(Boolean))],
    [audience],
  );

  const load = useCallback(
    async (reset: boolean) => {
      setLoading(true);
      try {
        const res = await audiencesApi.runResults(brandId, audience.audienceId, runId, {
          segmentId: segmentId || undefined,
          coverage: coverage || undefined,
          zone: zone || undefined,
          cursor: reset ? undefined : cursor,
          limit: 50,
        });
        setRows((prev) => (reset ? res.items : [...prev, ...res.items]));
        setCursor(res.nextCursor);
      } finally {
        setLoading(false);
      }
    },
    [brandId, audience.audienceId, runId, segmentId, coverage, zone, cursor],
  );

  // Reload on filter change.
  useEffect(() => {
    setRows([]);
    setCursor(undefined);
    load(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [segmentId, coverage, zone, runId]);

  const sorted = useMemo(() => {
    const arr = [...rows];
    const dir = sortAsc ? 1 : -1;
    arr.sort((a, b) => {
      if (sortKey === 'score') return ((a.score || 0) - (b.score || 0)) * dir;
      if (sortKey === 'engine') return a.engine.localeCompare(b.engine) * dir;
      return (a.coverage || '').localeCompare(b.coverage || '') * dir;
    });
    return arr;
  }, [rows, sortKey, sortAsc]);

  const sortHeader = (key: SortKey, label: string) => (
    <TableSortLabel
      active={sortKey === key}
      direction={sortKey === key && !sortAsc ? 'desc' : 'asc'}
      onClick={() => {
        if (sortKey === key) setSortAsc((v) => !v);
        else {
          setSortKey(key);
          setSortAsc(true);
        }
      }}
    >
      {label}
    </TableSortLabel>
  );

  return (
    <Box>
      <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} alignItems="center">
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Segment</InputLabel>
          <Select label="Segment" value={segmentId} onChange={(e) => setSegmentId(e.target.value)}>
            <MenuItem value="">Tous les segments</MenuItem>
            {audience.segments.map((s) => (
              <MenuItem key={s.segmentId} value={s.segmentId}>
                {s.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Couverture</InputLabel>
          <Select label="Couverture" value={coverage} onChange={(e) => setCoverage(e.target.value)}>
            <MenuItem value="">Toutes</MenuItem>
            <MenuItem value="brand_covered">Recommandé</MenuItem>
            <MenuItem value="assortment_leaked">Fuite</MenuItem>
            <MenuItem value="mentioned">Mentionné sans lien</MenuItem>
            <MenuItem value="not_covered">Absent</MenuItem>
          </Select>
        </FormControl>
        {zoneOptions.length > 0 && (
          <FormControl size="small" sx={{ minWidth: 160 }}>
            <InputLabel>Zone</InputLabel>
            <Select label="Zone" value={zone} onChange={(e) => setZone(e.target.value)}>
              <MenuItem value="">Toutes</MenuItem>
              {zoneOptions.map((z) => (
                <MenuItem key={z} value={z}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <ZoneFlag zone={resolveZone(z)} size={16} />
                    <span>{z}</span>
                  </Stack>
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Box sx={{ flex: 1 }} />
        <Typography variant="caption" color="text.secondary">
          {rows.length} réponse{rows.length !== 1 ? 's' : ''} chargée{rows.length !== 1 ? 's' : ''}
        </Typography>
      </Stack>

      {(segmentId || coverage || zone) && (
        <Stack direction="row" spacing={0.5} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
          {segmentId && (
            <Chip size="small" label={`Segment : ${segName(segmentId)}`} onDelete={() => setSegmentId('')} />
          )}
          {coverage && (
            <Chip
              size="small"
              label={`Couverture : ${(COVERAGE_META[coverage] || COVERAGE_META.not_covered).label}`}
              onDelete={() => setCoverage('')}
            />
          )}
          {zone && (
            <Chip
              size="small"
              icon={<ZoneFlag zone={resolveZone(zone)} size={14} />}
              label={`Zone : ${zone}`}
              onDelete={() => setZone('')}
            />
          )}
        </Stack>
      )}

      <Paper variant="outlined">
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>Prompt</TableCell>
              <TableCell>Segment</TableCell>
              <TableCell>{sortHeader('engine', 'Moteur')}</TableCell>
              <TableCell>{sortHeader('coverage', 'Couverture')}</TableCell>
              <TableCell align="center">{sortHeader('score', 'Score')}</TableCell>
              <TableCell align="center">Produits</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {sorted.map((r) => {
              const cov = COVERAGE_META[r.coverage] || COVERAGE_META.not_covered;
              return (
                <TableRow
                  key={`${r.segmentId}#${r.promptId}#${r.engine}`}
                  hover
                  sx={{ cursor: 'pointer' }}
                  onClick={() => setOpen(r)}
                >
                  <TableCell sx={{ maxWidth: 380 }}>
                    <Typography variant="body2" noWrap title={r.promptText}>
                      {r.promptText}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption">{segName(r.segmentId)}</Typography>
                  </TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={0.5} alignItems="center">
                      <EngineLogo engine={r.engine} size={16} />
                      {r.language && <Flag countryCode={languageFlag(r.language)} size={16} title={r.language.toUpperCase()} />}
                    </Stack>
                  </TableCell>
                  <TableCell>
                    {r.error ? (
                      <Chip size="small" sx={{ bgcolor: 'grey.300' }} label="Erreur moteur" />
                    ) : (
                      <Chip size="small" color={cov.color} label={cov.label} />
                    )}
                  </TableCell>
                  <TableCell align="center">{r.score}</TableCell>
                  <TableCell align="center">{r.recommendedProducts?.length || 0}</TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
        {sorted.length === 0 && !loading && (
          <Box sx={{ p: 3 }}>
            <Typography color="text.secondary">Aucune réponse.</Typography>
          </Box>
        )}
      </Paper>

      <Stack direction="row" justifyContent="center" sx={{ mt: 1.5 }}>
        {loading ? (
          <CircularProgress size={24} />
        ) : cursor ? (
          <Button onClick={() => load(false)}>Charger plus</Button>
        ) : (
          rows.length > 0 && (
            <Typography variant="caption" color="text.secondary">
              Fin des résultats
            </Typography>
          )
        )}
      </Stack>

      <ResponseDrawer
        brandId={brandId}
        audienceId={audience.audienceId}
        runId={runId}
        row={open}
        segmentName={open ? segName(open.segmentId) : ''}
        catalogueId={
          open ? audience.segments.find((s) => s.segmentId === open.segmentId)?.catalogueId : undefined
        }
        onClose={() => setOpen(null)}
      />
    </Box>
  );
}

// ------------------------------------------------------------------
// Response drawer — fetches the full result (content + sources) on open.
// ------------------------------------------------------------------
function ResponseDrawer({
  brandId,
  audienceId,
  runId,
  row,
  segmentName,
  catalogueId,
  onClose,
}: {
  brandId: string;
  audienceId: string;
  runId: string;
  row: MeasurementResult | null;
  segmentName: string;
  catalogueId?: string;
  onClose: () => void;
}) {
  const { show } = useToast();
  const [full, setFull] = useState<MeasurementResult | null>(null);
  const [loading, setLoading] = useState(false);
  const [savedIdeas, setSavedIdeas] = useState<Set<string>>(new Set());
  const [drawerTab, setDrawerTab] = useState<'response' | 'competitive'>('response');

  const saveIdea = async (title: string, detail: string) => {
    try {
      // Ground the reco in REAL catalogue products (verified URLs) so the article
      // grounds like catalog-mode instead of inventing product URLs when it later
      // runs in `reco` mode. Vector-search the catalogue for the prompt+idea text
      // (several products, not just one), falling back to the row's closestSku.
      let groundedProducts: GroundedProduct[] | undefined;
      if (catalogueId) {
        const q = [row?.promptText, title, detail].filter(Boolean).join(' ').slice(0, 400);
        // No type filter → products AND category pages; hydrate → friendly anchor.
        const hits = await retailerCatalogApi.search(brandId, catalogueId, q, undefined, 8, true).catch(() => []);
        groundedProducts = hits
          .filter((h) => h.url && h.name)
          .map((h) => ({
            productId: h.productId,
            name: h.name,
            url: h.url,
            ...(h.displayName ? { displayName: h.displayName } : {}),
          }));
      }
      if (!groundedProducts?.length) {
        const cs = full?.closestSku;
        groundedProducts =
          cs?.url && cs?.name ? [{ productId: cs.productId, name: cs.name, url: cs.url }] : undefined;
      }
      await proposedArticlesApi.createContentTopic({
        brandId,
        topic: detail ? `${title} — ${detail}` : title,
        originLabel: 'Analyse concurrentielle',
        ...(catalogueId ? { catalogueId } : {}),
        ...(groundedProducts?.length ? { groundedProducts } : {}),
      });
      setSavedIdeas((s) => new Set(s).add(title));
      show('Idée ajoutée à la création de contenu', { severity: 'success' });
    } catch (e: any) {
      show(e?.message || 'Ajout impossible', { severity: 'error' });
    }
  };

  useEffect(() => {
    if (!row) {
      setFull(null);
      return;
    }
    setDrawerTab('response'); // reset to the answer tab on each open
    setLoading(true);
    audiencesApi
      .runResult(brandId, audienceId, runId, row.segmentId, row.promptId, row.engine)
      .then(setFull)
      .finally(() => setLoading(false));
  }, [brandId, audienceId, runId, row]);

  const terms: HighlightTerm[] = useMemo(() => {
    if (!full) return [];
    // Single source of truth (item i): the worker emits the EXACT strings it
    // matched — render only those. Falls back to the old client-side
    // derivation for rows written before `matchedTerms` shipped.
    if (full.matchedTerms?.length) {
      return full.matchedTerms.map((m) => ({ term: m.term, cls: m.cls as HighlightTerm['cls'] }));
    }
    const t: HighlightTerm[] = [];
    for (const p of full.recommendedProducts || []) {
      if (p.name) t.push({ term: p.name, cls: 'product' });
      if (p.productId) t.push({ term: p.productId, cls: 'sku' });
    }
    if (full.retailerDomain) t.push({ term: full.retailerDomain.replace(/^www\./, ''), cls: 'retailer' });
    for (const h of full.sourceHosts || []) t.push({ term: h.replace(/^www\./, ''), cls: 'source' });
    return t;
  }, [full]);

  const cov = row ? COVERAGE_META[row.coverage] || COVERAGE_META.not_covered : null;

  return (
    <Drawer anchor="right" open={!!row} onClose={onClose} PaperProps={{ sx: { width: { xs: '100%', md: 620 } } }}>
      {row && (
        <Box sx={{ p: 2.5 }}>
          <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
            <Typography variant="h6" sx={{ flex: 1 }}>
              Réponse IA
            </Typography>
            <IconButton onClick={onClose} size="small">
              <CloseIcon />
            </IconButton>
          </Stack>

          <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            <Chip size="small" variant="outlined" icon={<EngineLogo engine={row.engine} size={14} />} label="" sx={{ '& .MuiChip-label': { pr: 0 } }} />
            <Chip size="small" variant="outlined" label={segmentName} />
            {cov && <Chip size="small" color={cov.color} label={cov.label} />}
            <Chip size="small" variant="outlined" label={`Score ${row.score}`} />
            {row.language && (
              <Chip
                size="small"
                variant="outlined"
                icon={<Flag countryCode={languageFlag(row.language)} size={14} />}
                label={row.language.toUpperCase()}
              />
            )}
            {row.zone && (
              <Chip
                size="small"
                variant="outlined"
                icon={<ZoneFlag zone={resolveZone(row.zone)} size={14} />}
                label={row.zone}
              />
            )}
          </Stack>

          <Paper variant="outlined" sx={{ p: 1.5, mb: 2, bgcolor: 'action.hover' }}>
            <Typography variant="caption" color="text.secondary">Prompt</Typography>
            <Typography variant="body2">{row.promptText}</Typography>
          </Paper>

          {(() => {
            const hasCompetitive = !!full?.competitive && !full.competitive.retailerIsWinner;
            const recCount = full?.competitive?.recommendations?.length || 0;
            return (
              <Tabs
                value={drawerTab}
                onChange={(_, v) => setDrawerTab(v)}
                sx={{ mb: 1.5, borderBottom: 1, borderColor: 'divider', minHeight: 40 }}
              >
                <Tab label="Réponse" value="response" sx={{ minHeight: 40 }} />
                <Tab
                  value="competitive"
                  disabled={!hasCompetitive}
                  sx={{ minHeight: 40 }}
                  label={
                    <Badge badgeContent={recCount} color="secondary" sx={{ '& .MuiBadge-badge': { right: -12 } }}>
                      Analyse concurrentielle
                    </Badge>
                  }
                />
              </Tabs>
            );
          })()}

          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
              <CircularProgress size={28} />
            </Box>
          ) : drawerTab === 'response' ? (
            <>
              {full?.error ? (
                <Alert severity="error" sx={{ mb: 1.5 }}>
                  Le moteur a échoué de façon définitive pour cette requête : {full.error}
                </Alert>
              ) : (
                <Stack direction="row" spacing={1} sx={{ mb: 1 }} flexWrap="wrap" useFlexGap>
                  <LegendChip cls="product" label="Produit" />
                  <LegendChip cls="sku" label="Référence" />
                  <LegendChip cls="retailer" label="Votre domaine" />
                  <LegendChip cls="source" label="Source citée" />
                </Stack>
              )}
              {!full?.error && (
                <Paper variant="outlined" sx={{ p: 1.5, fontSize: '0.875rem', lineHeight: 1.7 }}>
                  {full?.content ? renderMarkdown(full.content, terms) : (
                    <Typography variant="body2" color="text.secondary">
                      (réponse non disponible — relancez une analyse)
                    </Typography>
                  )}
                </Paper>
              )}

              {full?.recommendedProducts && full.recommendedProducts.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Produits détectés
                  </Typography>
                  <Table size="small">
                    <TableBody>
                      {[...full.recommendedProducts]
                        .sort(
                          (a, b) =>
                            ((a.linkOrder ?? a.position) || 999) -
                            ((b.linkOrder ?? b.position) || 999),
                        )
                        .map((p) => {
                          const rank = p.linkOrder ?? p.position;
                          return (
                            <TableRow key={p.productId}>
                              <TableCell>
                                {p.citedUrl ? (
                                  <Link
                                    href={p.citedUrl}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}
                                  >
                                    {p.name || p.productId}
                                    <OpenInNewIcon sx={{ fontSize: 14 }} />
                                  </Link>
                                ) : (
                                  p.name || p.productId
                                )}
                              </TableCell>
                              <TableCell>
                                <Stack direction="row" spacing={0.5}>
                                  {(p.detectedBy ?? [p.matchedBy]).map((s) => (
                                    <Chip
                                      key={s}
                                      size="small"
                                      variant="outlined"
                                      label={s}
                                      sx={
                                        SIGNAL_ACCENT_COLOR[s as CatalogueMatchSignal]
                                          ? {
                                              color: SIGNAL_ACCENT_COLOR[s as CatalogueMatchSignal],
                                              borderColor: SIGNAL_ACCENT_COLOR[s as CatalogueMatchSignal],
                                            }
                                          : undefined
                                      }
                                    />
                                  ))}
                                </Stack>
                              </TableCell>
                              <TableCell align="right">{rank ? `#${rank}` : '—'}</TableCell>
                            </TableRow>
                          );
                        })}
                    </TableBody>
                  </Table>
                  <Typography variant="caption" color="text.secondary">
                    Éléments détectés dans la réponse
                  </Typography>
                </Box>
              )}

              {full?.closestSku && (
                <Box sx={{ mt: 2 }}>
                  <Divider sx={{ mb: 1 }} />
                  <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                    Votre produit le plus proche
                  </Typography>
                  <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2">{full.closestSku.name}</Typography>
                      <Link href={full.closestSku.url.startsWith('http') ? full.closestSku.url : `https://${full.closestSku.url}`} target="_blank" rel="noopener" variant="caption">
                        {full.closestSku.url}
                      </Link>
                    </Box>
                    <Chip size="small" label={`proximité ${Math.round((full.closestSku.score || 0) * 100)}%`} />
                  </Paper>
                </Box>
              )}

              {(() => {
                // Prefer full URLs; fall back to bare hosts (Gemini cites hosts only).
                const urls = full?.sourceUrls || [];
                const hosts = full?.sourceHosts || [];
                const items = urls.length ? urls : hosts;
                if (!items.length) return null;
                return (
                  <Box sx={{ mt: 2 }}>
                    <Divider sx={{ mb: 1 }} />
                    <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                      Sources citées ({items.length})
                    </Typography>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                      {items.map((u) => {
                        const href = u.startsWith('http') ? u : `https://${u}`;
                        const isRetailer =
                          !!full?.retailerDomain &&
                          u.replace(/^https?:\/\//, '').replace(/^www\./, '').startsWith(
                            full.retailerDomain.replace(/^www\./, ''),
                          );
                        return (
                          <Chip
                            key={u}
                            size="small"
                            component="a"
                            clickable
                            href={href}
                            target="_blank"
                            rel="noopener"
                            color={isRetailer ? 'success' : 'default'}
                            variant={isRetailer ? 'filled' : 'outlined'}
                            icon={<OpenInNewIcon sx={{ fontSize: 12 }} />}
                            label={u.replace(/^https?:\/\//, '').replace(/^www\./, '').slice(0, 48)}
                          />
                        );
                      })}
                    </Stack>
                  </Box>
                );
              })()}
            </>
          ) : (
            /* ---- Analyse concurrentielle tab ---- */
            <CompetitiveTab
              competitive={full?.competitive}
              closestSku={full?.closestSku}
              savedIdeas={savedIdeas}
              onSaveIdea={saveIdea}
            />
          )}
        </Box>
      )}
    </Drawer>
  );
}

/** Analyse concurrentielle — why #1 wins + prioritized fixes, in its own drawer tab. */
function CompetitiveTab({
  competitive,
  closestSku,
  savedIdeas,
  onSaveIdea,
}: {
  competitive?: MeasurementResult['competitive'];
  closestSku?: MeasurementResult['closestSku'];
  savedIdeas: Set<string>;
  onSaveIdea: (title: string, detail: string) => void;
}) {
  if (!competitive || competitive.retailerIsWinner) {
    return (
      <Alert severity="success" icon={false}>
        Vous êtes la référence recommandée sur cette requête — rien à corriger.
      </Alert>
    );
  }
  if (competitive.noCommercialWinner) {
    return (
      <Alert severity="info" icon={false}>
        Aucun concurrent commercial cité dans cette réponse — rien à analyser.
      </Alert>
    );
  }
  return (
    <Stack spacing={2}>
      {competitive.winnerName && (
        <Paper variant="outlined" sx={{ p: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Recommandation #1
          </Typography>
          <Stack direction="row" alignItems="center" spacing={1}>
            <Typography variant="body2" fontWeight={700} sx={{ flex: 1 }}>
              {competitive.winnerName}
            </Typography>
            {competitive.winnerUrl && (
              <Link href={competitive.winnerUrl} target="_blank" rel="noopener" variant="caption" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5 }}>
                Voir <OpenInNewIcon sx={{ fontSize: 13 }} />
              </Link>
            )}
          </Stack>
        </Paper>
      )}

      {(competitive.whyWinsBullets?.length || competitive.whyWins) && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Pourquoi le #1 gagne
          </Typography>
          <Alert severity="info" icon={false}>
            {competitive.whyWinsBullets?.length ? (
              <Stack component="ul" sx={{ m: 0, pl: 2 }} spacing={0.25}>
                {competitive.whyWinsBullets.map((b, i) => (
                  <Typography component="li" variant="body2" key={i}>{b}</Typography>
                ))}
              </Stack>
            ) : (
              <Typography variant="body2">{competitive.whyWins}</Typography>
            )}
          </Alert>
          {!competitive.winnerFetched && competitive.winnerUrl && (
            <Typography variant="caption" color="text.secondary">
              (page du #1 non récupérée — analyse basée sur la réponse)
            </Typography>
          )}
        </Box>
      )}

      {!!competitive.whatToChange?.length && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Ce qu'il faut changer
          </Typography>
          <Stack component="ul" sx={{ m: 0, pl: 2 }} spacing={0.25}>
            {competitive.whatToChange.map((b, i) => (
              <Typography component="li" variant="body2" color="text.secondary" key={i}>{b}</Typography>
            ))}
          </Stack>
        </Box>
      )}

      {closestSku && (
        <Paper variant="outlined" sx={{ p: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="caption" color="text.secondary">Votre produit à pousser</Typography>
            <Typography variant="body2">{closestSku.name}</Typography>
          </Box>
          <Chip size="small" label={`proximité ${Math.round((closestSku.score || 0) * 100)}%`} />
        </Paper>
      )}

      {competitive.recommendations.length > 0 && (
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
            Recommandations
          </Typography>
          <Stack spacing={1}>
            {competitive.recommendations.map((r, i) => (
              <Paper key={i} variant="outlined" sx={{ p: 1.25 }}>
                <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                  <Chip
                    size="small"
                    color={r.kind === 'product_change' ? 'primary' : 'secondary'}
                    label={r.kind === 'product_change' ? 'Fiche produit' : 'Contenu'}
                  />
                  <Typography variant="body2" fontWeight={600}>{r.title}</Typography>
                </Stack>
                <Typography variant="body2" color="text.secondary">{r.detail}</Typography>
                {r.kind === 'product_change' && (
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                    → à appliquer dans « Optimisation produit » (filtre « stratégique »)
                  </Typography>
                )}
                {r.kind === 'content_idea' && (
                  <Button
                    size="small"
                    sx={{ mt: 0.5 }}
                    disabled={savedIdeas.has(r.title)}
                    onClick={() => onSaveIdea(r.title, r.detail)}
                  >
                    {savedIdeas.has(r.title) ? '✓ Ajouté au contenu' : '→ Créer un contenu'}
                  </Button>
                )}
              </Paper>
            ))}
          </Stack>
        </Box>
      )}
    </Stack>
  );
}

function LegendChip({ cls, label }: { cls: HighlightTerm['cls']; label: string }) {
  return <Box component="span">{highlightText(label, [{ term: label, cls }])}</Box>;
}
